import React, { useState, useEffect } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import styled from 'styled-components';
import {
  TableRow,
  TableHead,
  TableCell,
  TableBody,
  Table,
  Card,
  CardContent,
  Typography,
} from '@material-ui/core';
import { get, isEmpty, uniqueId } from 'lodash';
import messages from 'containers/ManagerHomePage/messages';
import { formatLossdate } from 'containers/CourtManagerAssignedClaims/utils';
import { translate } from 'containers/services/translate';
import { AssignedClaim } from 'containers/CourtManagerAssignedClaims/typings';
import routesHelper from 'containers/services/routes-helper';
import { PAGES } from 'routes';
import { COLORS } from 'constants/design';
import { SagisConfig } from 'containers/AppContainer/typings';
import * as utils from 'containers/RecapManagerPage/utils';
import SecondaryButton from 'components/Buttons/SecondaryButton';
import userPrivileges, {
  PRIVILEGES,
} from 'containers/services/security/user-privileges';
import {
  formatAssignedClaims,
  renderUrlByProfile,
  renderClaimStatus,
  filterEmptyResult,
} from 'containers/ManagerHomePage/utils';
import { CLAIM_STATUS } from 'containers/data-structure';
import { FEATURES } from 'features/features';
import * as featureService from 'features/fflip-helpers';
import { sagisClaimStatuses } from 'containers/ManagerHomePage/constants';

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: auto;
  padding-top: 50px;

  .summary-button {
    background: ${COLORS.WHITE} 0% 0% no-repeat padding-box;
    border: 1px solid ${COLORS.GREY};
    &:hover {
      background-color: ${COLORS.WHITE};
    }
  }

  .recap {
    background: transparent
      linear-gradient(229deg, #707891 0%, #0b7cc5 0%, #56afbb 88%, #5db4ba 100%)
      0% 0% no-repeat padding-box;
    opacity: 1;
    width: 46px;
    height: 46px;
    border-radius: 300px;
    justify-content: center;
    display: flex;
    align-items: center;
  }
  .titleContainer {
    display: flex;
  }
  .recapValue {
    font-family: Roboto;
    font-weight: Bold;
    font-size: 18px;
    letter-spacing: 0;
    color: ${COLORS.WHITE};
    opacity: 1;
  }
  .title {
    text-align: left;
    font-family: Roboto;
    font-weight: Bold;
    font-size: 18px;
    letter-spacing: 0;
    color: #384264;
    opacity: 1;
    padding: 10px;
  }
`;

export interface AssignedClaimsTableProps {
  intl: IntlShape;
  pendingClaims: AssignedClaim[];
  lawyers: SagisConfig[];
  lastAssignedClaims: AssignedClaim[];
  isCourtMonitoringManager: boolean;
  isConsultationManager: boolean;
  isOpeningManager: boolean;
  isTransactionManager: boolean;
  setSelectedResultSearch: Function;
  resetTabsIndex: Function;
  requestSearchClaims: Function;
}

const tableHeaders = [
  messages.claimNature,
  messages.claimNumber,
  messages.policyNumber,
  messages.registrationNumber,
  messages.lossDate,
  messages.status,
  messages.assignedLawyer,
];

const tableHeadersForCourtManager = [
  messages.claimNature,
  messages.claimNumber,
  messages.policyNumber,
  messages.registrationNumber,
  messages.lossDate,
  messages.bawabaStatus,
  messages.sagisStatus,
  messages.assignedLawyer,
];

const AssignedClaimsTable: React.FC<AssignedClaimsTableProps> = (
  props,
): JSX.Element => {
  const [claims, setClaims] = useState<AssignedClaim[]>([]);
  useEffect(() => {
    if (userPrivileges.userHasPrivilege(PRIVILEGES.ISCOURTMANAGER))
      setClaims(
        props.lastAssignedClaims.map((claim: any) => ({
          ...claim,
        })),
      );
    else
      setClaims(
        props.pendingClaims.map((claim: any) => ({
          ...claim,
          assignedCourtManager: get(
            claim,
            'assignment[0].assignedCourtManager',
          ),
          assignedLawyer: get(claim, 'assignment[0].assignedLawyer'),
        })),
      );
  }, [props.pendingClaims, props.lastAssignedClaims]);
  const headers =
    featureService.hasFeature(FEATURES.EVOLUTION_P1) &&
    userPrivileges.userHasPrivilege(PRIVILEGES.ISCOURTMANAGER)
      ? tableHeadersForCourtManager
      : tableHeaders;

  return (
    <Container>
      <Card>
        <CardContent>
          <Typography className="titleContainer">
            <div className="recap">
              <span className="recapValue">
                {filterEmptyResult(claims).length}
              </span>
            </div>
            <span className="title">
              {translate(messages.tableTitle, props)}
            </span>
          </Typography>
          <div>
            <Table>
              <TableHead>
                <TableRow>
                  {headers.map(header => (
                    <TableCell
                      key={uniqueId('header')}
                      align="left"
                      className="headerTitle"
                    >
                      {translate(header, props)}
                    </TableCell>
                  ))}
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {!isEmpty(filterEmptyResult(claims)) &&
                  formatAssignedClaims(filterEmptyResult(claims), true).map(
                    (assignment: any) => (
                      <TableRow>
                        <TableCell align="left">
                          {assignment?.nature
                            ? assignment.nature
                            : assignment &&
                              translate(messages.claimNatureCorporeal, props)}
                        </TableCell>
                        <TableCell align="left">
                          {get(assignment, 'claimNumber')}
                        </TableCell>
                        <TableCell align="left">
                          {get(assignment, 'policyNumber') ||
                            get(assignment.existingClaim, 'policyNumber')}
                        </TableCell>
                        <TableCell align="left">
                          {get(assignment, 'registrationNumber')}
                        </TableCell>
                        <TableCell align="left">
                          {formatLossdate(
                            get(assignment, 'lossDate'),
                            get(assignment, 'lossHour'),
                          )}
                        </TableCell>
                        <TableCell align="left">
                          {renderClaimStatus(get(assignment, 'status'))
                            ? translate(
                                renderClaimStatus(get(assignment, 'status')),
                                props,
                              )
                            : ''}
                        </TableCell>
                        {featureService.hasFeature(FEATURES.EVOLUTION_P1) &&
                          userPrivileges.userHasPrivilege(
                            PRIVILEGES.ISCOURTMANAGER,
                          ) && (
                            <TableCell align="left">
                              {get(assignment, 'status') &&
                              sagisClaimStatuses.includes(
                                get(assignment, 'status'),
                              )
                                ? get(assignment, 'status')
                                : ''}
                            </TableCell>
                          )}
                        <TableCell align="left">
                          {!isEmpty(get(assignment, 'assignment'))
                            ? get(assignment, 'assignment', []).map(
                                /* istanbul ignore next */ (el: any) =>
                                  utils.getLawyerById(
                                    props.lawyers,
                                    el.assignedLawyer as string,
                                  )
                                    ? `${utils.getLawyerById(
                                        props.lawyers,
                                        el.assignedLawyer as string,
                                      )}, `
                                    : '',
                              )
                            : utils.getLawyerById(
                                props.lawyers,
                                assignment.assignedLawyer as string,
                              )}
                        </TableCell>
                        <TableCell>
                          {(featureService.hasFeature(FEATURES.EVOLUTION_P3) ? (
                            !props.isOpeningManager
                          ) : (
                            true
                          )) ? (
                            <SecondaryButton
                              title={
                                get(assignment, 'isBasicManagement') &&
                                ![
                                  CLAIM_STATUS.VALIDATED,
                                  CLAIM_STATUS.PENDING_INFO_MODIFICATION,
                                  CLAIM_STATUS.PENDING,
                                  CLAIM_STATUS.REJECTED,
                                ].includes(get(assignment, 'status'))
                                  ? translate(
                                      { ...messages.basicManagement },
                                      props,
                                    )
                                  : translate({ ...messages.summary }, props)
                              }
                              onClick={() => {
                                if (
                                  get(assignment, 'isBasicManagement') &&
                                  ![
                                    CLAIM_STATUS.VALIDATED,
                                    CLAIM_STATUS.PENDING_INFO_MODIFICATION,
                                    CLAIM_STATUS.PENDING,
                                    CLAIM_STATUS.REJECTED,
                                  ].includes(get(assignment, 'status'))
                                ) {
                                  props.requestSearchClaims({
                                    claimNumber: get(assignment, 'claimNumber'),
                                    withDocuments: 'Y',
                                  });
                                  routesHelper.pushTo(
                                    PAGES.PREOPENING_MANAGER,
                                    {
                                      isFromSagis: true,
                                      sagisInfoClaim: assignment,
                                    },
                                  )();
                                } else {
                                  props.setSelectedResultSearch(assignment);
                                  props.resetTabsIndex();
                                  routesHelper.pushTo(
                                    renderUrlByProfile(
                                      props.isCourtMonitoringManager,
                                      props.isConsultationManager,
                                      props.isOpeningManager,
                                      props.isTransactionManager,
                                      assignment.id,
                                      get(assignment, 'status'),
                                      get(assignment, 'openingState'),
                                    ),
                                  )();
                                }
                              }}
                            />
                          ) : get(assignment, 'isBasicManagement') &&
                            ![
                              CLAIM_STATUS.VALIDATED,
                              CLAIM_STATUS.PENDING_INFO_MODIFICATION,
                              CLAIM_STATUS.PENDING,
                              CLAIM_STATUS.REJECTED,
                            ].includes(get(assignment, 'status')) ? (
                            <SecondaryButton
                              title={translate(
                                messages.basicManagementTitle,
                                props,
                              )}
                              onClick={() => {}}
                            />
                          ) : (
                            <SecondaryButton
                              title={translate({ ...messages.summary }, props)}
                              onClick={() => {
                                props.setSelectedResultSearch(assignment);
                                props.resetTabsIndex();
                                routesHelper.pushTo(
                                  renderUrlByProfile(
                                    props.isCourtMonitoringManager,
                                    props.isConsultationManager,
                                    props.isOpeningManager,
                                    props.isTransactionManager,
                                    assignment.id,
                                    get(assignment, 'status'),
                                    get(assignment, 'openingState'),
                                  ),
                                )();
                              }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ),
                  )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </Container>
  );
};

export default injectIntl(AssignedClaimsTable);
export { AssignedClaimsTable as AssignedClaimsTableComponent };
